import {
  enumDispatchRecordStatus,
  type DispatchRecordStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  Menu,
  Row,
  SearchField,
  Select,
  TinyTable,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideAlertCircle,
  LucideBuilding,
  LucideCalendar,
  LucideEllipsis,
  LucideGlobe,
  LucideHash,
  LucideRefreshCw,
  LucideShield,
} from 'lucide-react';

import { DispatchRecordEntry } from '../../_graphql/queryDispatchRecords';
import * as dispatchState from '../../_state/dispatchRecords.state';
import { $rawGovReportingConfigs } from '../../_state/govReporting.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';
import { SettingsContent } from '../_shared/SettingsContent';

const STATUS_BADGE_CLASSES: Record<DispatchRecordStatus, string> = {
  Queued: 'text-neutral/60',
  Processing: 'text-info',
  Submitted: 'text-success',
  Failed: 'text-failure',
  NotReported: 'text-warning',
};

type DispatchRowMenuProps = { row: DispatchRecordEntry };

const DispatchRowMenu = (props: DispatchRowMenuProps) => {
  const t = useTranslations();
  const retry = useSetAtom(dispatchState.$retryDispatchRecord);
  const toast = useToast();

  const handleRetry = async () => {
    try {
      await retry({ id: props.row.id });
      toast.success(t.complianceView.dispatchHistory.messages.retrySuccess);
    } catch (error) {
      toast.error(
        t.complianceView.dispatchHistory.messages.retryError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  if (
    !props.row.isRetriable ||
    props.row.status !== enumDispatchRecordStatus.Failed
  ) {
    return null;
  }

  return (
    <div className="@DispatchRowMenu contents">
      <Menu
        trigger={
          <Button
            className="px-2"
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: t.complianceView.dispatchHistory.actions.retry,
            icon: <LucideRefreshCw />,
            onAction: handleRetry,
          },
        ]}
      />
    </div>
  );
};

export const DispatchHistoryListView = () => {
  const t = useTranslations();
  const records = useAtomValue(dispatchState.$dispatchRecords);
  const configs = useAtomValue($rawGovReportingConfigs);
  const [searchTerm, setSearchTerm] = useAtom(
    dispatchState.$dispatchRecordSearchTerm.value,
  );
  const [statusFilter, setStatusFilter] = useAtom(
    dispatchState.$dispatchRecordStatusFilter,
  );
  const [configFilter, setConfigFilter] = useAtom(
    dispatchState.$dispatchRecordConfigFilter,
  );

  const statusOptions = [
    {
      key: '',
      label: t.complianceView.dispatchHistory.filters.allStatuses,
    },
    ...Object.values(enumDispatchRecordStatus).map((status) => ({
      key: status,
      label:
        t.complianceView.dispatchHistory.statuses[
          status as keyof typeof t.complianceView.dispatchHistory.statuses
        ] ?? status,
    })),
  ];

  const configOptions = [
    {
      key: '',
      label: t.complianceView.dispatchHistory.filters.allConfigurations,
    },
    ...(configs.data ?? []).map((config) => ({
      key: config.id,
      label: config.name,
    })),
  ];

  const selectedStatusKey = statusFilter.length === 1 ? statusFilter[0] : '';

  return (
    <Col className="@DispatchHistoryListView h-full">
      <SettingsSubmodule
        title={t.complianceView.dispatchHistory.title}
        subtitle={t.complianceView.dispatchHistory.subtitle}
        tabs={null}
      >
        <SettingsContent className="h-full">
          <Col gap4 className="h-full">
            <Row gap2>
              <SearchField
                value={searchTerm}
                onChange={setSearchTerm}
                autoFocus
                placeholder={
                  t.complianceView.dispatchHistory.search.placeholder
                }
              />
              <Select
                label={t.complianceView.dispatchHistory.filters.status}
                value={selectedStatusKey}
                onChange={(value) => {
                  setStatusFilter(value ? [value as DispatchRecordStatus] : []);
                }}
                options={statusOptions}
              />
              <Select
                label={t.complianceView.dispatchHistory.filters.configuration}
                value={configFilter ?? ''}
                onChange={(value) => {
                  setConfigFilter(value || undefined);
                }}
                options={configOptions}
              />
            </Row>
            <TinyTable
              rowKey={(row) => row.id}
              isLoading={records.isLoading}
              error={records.error}
              columns={[
                {
                  key: 'facility',
                  label: t.complianceView.dispatchHistory.columns.facility,
                  icon: LucideBuilding,
                  cell: (row) => (
                    <div className="truncate font-medium">
                      {row.facilityName || '–'}
                    </div>
                  ),
                },
                {
                  key: 'configuration',
                  label: t.complianceView.dispatchHistory.columns.configuration,
                  icon: LucideGlobe,
                  width: 200,
                  cell: (row) => (
                    <Col className="min-w-0">
                      <div className="truncate font-medium">
                        {row.configName}
                      </div>
                      {row.serviceType && (
                        <div className="truncate text-xs text-neutral/50">
                          {row.serviceType}
                        </div>
                      )}
                    </Col>
                  ),
                },
                {
                  key: 'status',
                  label: t.complianceView.dispatchHistory.columns.status,
                  icon: LucideShield,
                  width: 120,
                  cell: (row) => (
                    <span
                      className={cn(
                        'font-medium',
                        STATUS_BADGE_CLASSES[
                          row.status as DispatchRecordStatus
                        ] ?? 'text-neutral/60',
                      )}
                    >
                      {t.complianceView.dispatchHistory.statuses[
                        row.status as keyof typeof t.complianceView.dispatchHistory.statuses
                      ] ?? row.status}
                    </span>
                  ),
                },
                {
                  key: 'batchId',
                  label: t.complianceView.dispatchHistory.columns.batchId,
                  icon: LucideHash,
                  width: 160,
                  cell: (row) =>
                    row.batchId ? (
                      <code className="rounded bg-neutral/5 px-1.5 py-0.5 text-xs">
                        {row.batchId}
                      </code>
                    ) : (
                      <span className="text-neutral/40">–</span>
                    ),
                },
                {
                  key: 'submittedAt',
                  label: t.complianceView.dispatchHistory.columns.submittedAt,
                  icon: LucideCalendar,
                  width: 150,
                  cell: (row) => (
                    <div className="truncate">
                      {row.submittedAt
                        ? new Date(
                            row.submittedAt.replace(/\[.*\]$/, ''),
                          ).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '–'}
                    </div>
                  ),
                },
                {
                  key: 'errorMessage',
                  label: t.complianceView.dispatchHistory.columns.errorMessage,
                  icon: LucideAlertCircle,
                  width: 200,
                  cell: (row) =>
                    row.errorMessage ? (
                      <div
                        className="truncate text-xs text-failure/80"
                        title={row.errorMessage}
                      >
                        {row.errorMessage}
                      </div>
                    ) : (
                      <span className="text-neutral/40">–</span>
                    ),
                },
                {
                  key: 'retries',
                  label: t.complianceView.dispatchHistory.columns.retries,
                  width: 80,
                  cell: (row) => (
                    <span
                      className={cn(
                        row.retryCount > 0 && 'text-warning',
                        row.retryCount === 0 && 'text-neutral/40',
                      )}
                    >
                      {row.retryCount} / {row.maxRetryCount}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  label: null,
                  preventRowClick: true,
                  width: 'max-content',
                  cell: (row) => <DispatchRowMenu row={row} />,
                },
              ]}
              data={records.data ?? []}
            />
            {!records.isLoading &&
              !records.error &&
              (records.data ?? []).length === 0 && (
                <Col className="items-center py-8 text-center">
                  <div className="text-sm font-medium text-neutral/60">
                    {t.complianceView.dispatchHistory.emptyState.title}
                  </div>
                  <div className="text-sm text-neutral/40">
                    {t.complianceView.dispatchHistory.emptyState.description}
                  </div>
                </Col>
              )}
          </Col>
        </SettingsContent>
      </SettingsSubmodule>
    </Col>
  );
};
